import React, { useState, useContext, Fragment, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { ServerConfigContext } from "../context/ServerConfigContext";
import { getRateColorClass } from "../utils/formatUtils";
import { createApiService } from "../utils/apiService";
import StarButton from "./StarButton";
import "../pages/TradingPages.css";

const TradingTable = ({
  pageId = "Unknown",
  data,
  isLoading,
  isSearching,
  loadingMessage = "데이터를 조회하고 있습니다...",
  initialLoadingMessage = "초기 데이터를 로딩하고 있습니다...",
  showSettings,
  setShowSettings,
  visibleDetails,
  setVisibleDetails,
  chartSettings,
  setChartSettings,
  activeTab,
  setActiveTab,
  selectedDate,
  showDetailsSection = true,
  setShowDetailsSection = () => {},
  topThemes = [], // 상위 테마 리스트 (테마 페이지/실시간 페이지에서 전달)
  showStarButton = false, // 즐겨찾기 별 버튼 표시 여부
  enableExpand, // FavoritesPage에서 전달
  expandContent, // FavoritesPage에서 전달
  hiddenColumns = [], // CSS로 숨긴 컬럼 이름 배열 (예: ['trading-value', 'volume'])
}) => {
  const { tableSettings, getServer } = useContext(ServerConfigContext);
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [chartDataCache, setChartDataCache] = useState({});
  const [chartLoadingStates, setChartLoadingStates] = useState({});
  const [programDataCache, setProgramDataCache] = useState({}); // 프로그램매매 데이터 캐시
  const [programLoadingStates, setProgramLoadingStates] = useState({}); // 프로그램매매 로딩 상태
  const [fngDataCache, setFngDataCache] = useState({}); // FNG 요약 데이터 캐시
  const [fngLoadingStates, setFngLoadingStates] = useState({}); // FNG 데이터 로딩 상태
  const [foreignInstDataCache, setForeignInstDataCache] = useState({}); // 외국인/기관 매매 데이터 캐시
  const [foreignInstLoadingStates, setForeignInstLoadingStates] = useState({}); // 외국인/기관 데이터 로딩 상태
  const [newsDataCache, setNewsDataCache] = useState({}); // StockPlus 뉴스 데이터 캐시
  const [newsLoadingStates, setNewsLoadingStates] = useState({}); // 뉴스 데이터 로딩 상태

  // 데이터가 변경될 때 확장된 행과 캐시 초기화
  useEffect(() => {
    setExpandedRowIds([]);
    setChartDataCache({});
    setChartLoadingStates({});
    setProgramDataCache({});
    setProgramLoadingStates({});
    setFngDataCache({});
    setFngLoadingStates({});
    setForeignInstDataCache({});
    setForeignInstLoadingStates({});
    setNewsDataCache({});
    setNewsLoadingStates({});
  }, [data]);

  // 테마 색상 정의 (4가지 색상 패턴)
  const themeColorSchemes = [
    {
      bg: "#e3f2fd",
      color: "#1976d2",
      border: "#bbdefb",
      hoverBg: "#bbdefb",
      hoverBorder: "#90caf9",
    }, // 파랑
    {
      bg: "#f3e5f5",
      color: "#7b1fa2",
      border: "#ce93d8",
      hoverBg: "#ce93d8",
      hoverBorder: "#ba68c8",
    }, // 보라
    {
      bg: "#e8f5e8",
      color: "#388e3c",
      border: "#a5d6a7",
      hoverBg: "#a5d6a7",
      hoverBorder: "#81c784",
    }, // 녹색
    {
      bg: "#fff3e0",
      color: "#f57c00",
      border: "#ffcc02",
      hoverBg: "#ffcc02",
      hoverBorder: "#ffa000",
    }, // 주황
  ];

  // 테마 이름을 기반으로 일관된 색상 인덱스를 반환하는 함수
  const getThemeColorIndex = (themeName) => {
    if (!topThemes || topThemes.length === 0) {
      // topThemes가 없으면 테마 이름의 해시값으로 색상 결정
      let hash = 0;
      for (let i = 0; i < themeName.length; i++) {
        hash = themeName.charCodeAt(i) + ((hash << 5) - hash);
      }
      return Math.abs(hash) % themeColorSchemes.length;
    }

    // topThemes에서 테마의 인덱스를 찾아서 색상 결정
    const index = topThemes.indexOf(themeName);
    if (index >= 0) {
      return index % themeColorSchemes.length;
    }

    // topThemes에 없는 테마는 이름의 해시값으로 색상 결정
    let hash = 0;
    for (let i = 0; i < themeName.length; i++) {
      hash = themeName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % themeColorSchemes.length;
  };

  // 테마별 색상 스타일을 반환하는 함수
  const getThemeStyle = (themeName) => {
    const colorIndex = getThemeColorIndex(themeName);
    const scheme = themeColorSchemes[colorIndex];
    return {
      backgroundColor: scheme.bg,
      color: scheme.color,
      borderColor: scheme.border,
    };
  };

  // 날짜에서 N일 이전 날짜 계산 함수
  const calculateStartDate = (endDate, daysBack) => {
    const date = new Date(
      endDate.substring(0, 4),
      parseInt(endDate.substring(4, 6)) - 1,
      endDate.substring(6, 8)
    );
    date.setDate(date.getDate() - daysBack);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}${month}${day}`;
  };

  const fetchChartData = async (stockCode, chartType, edate) => {
    try {
      const server = getServer("chart-api-server");
      if (!server || !server.isActive) {
        console.error("chart-api-server를 찾을 수 없거나 비활성 상태입니다.");
        return null;
      }

      const apiService = createApiService(server);

      // 차트 타입에 따라 endpoint와 파라미터 설정
      let endpoint, params;

      if (chartType === "daily") {
        // 일봉: /api/v1/stocks/chart, gubun=2, edate 기준 과거 60일 데이터 조회
        endpoint = `/api/v1/charts/daily/${stockCode}`;
        // endpoint = `/api/v1/stocks/chart`;
        const sdate = calculateStartDate(edate, 90);
        params = {
          shcode: stockCode,
          // gubun: "2",
          // sdate,
          edate,
        };
        console.log(`📊 일봉 조회: ${stockCode} (${sdate} ~ ${edate})`);
      } else if (chartType === "minute") {
        // 분봉: /api/v1/stocks/minute-chart
        endpoint = `/api/v1/charts/minute/${stockCode}`;
        // endpoint = `/api/v1/stocks/minute-chart`;
        params = {
          shcode: stockCode,
          sdate: edate,
          edate,
        };
        console.log(`📊 분봉 조회: ${stockCode} (${edate})`);
      }

      // POST 요청 (Query Parameter 방식)
      const response = await apiService.get(endpoint, params);

      // 응답에서 배열 추출 (일봉: t8410OutBlock1, 분봉: t8412OutBlock1)
      const blockKey =
        chartType === "daily" ? "t8410OutBlock1" : "t8412OutBlock1";

      if (response) return response;

      // if (response && response[blockKey]) {
      //   return response[blockKey];
      // } else if (response && response.data && response.data[blockKey]) {
      //   return response.data[blockKey];
      // } else if (response && response.data && !(blockKey in response.data)) {
      //   return response.data;
      // }

      console.warn(`예상하지 못한 응답 형식 (${chartType}):`, response);
      return null;
    } catch (error) {
      console.error(`${chartType} 차트 데이터 조회 실패:`, error);
      return null;
    }
  };

  // 프로그램매매 데이터 조회 함수
  const fetchProgramData = async (stockCode, date) => {
    try {
      const server = getServer("stock-api-server");
      if (!server || !server.isActive) {
        console.error("stock-api-server를 찾을 수 없거나 비활성 상태입니다.");
        return null;
      }

      const apiService = createApiService(server);
      const endpoint = `/api/v1/trade/program/${stockCode}`;

      // date를 yyyyMMdd 형식으로 변환 (이미 변환된 경우도 있음)
      const formattedDate = date.replace(/-/g, '');

      const params = {
        date: formattedDate
      };

      console.log(`📊 프로그램매매 조회: ${stockCode}, date: ${formattedDate}`);
      let response = await apiService.get(endpoint, params);

      console.log("📊 프로그램매매 응답 원본:", response);
      console.log("📊 응답 타입:", typeof response);

      // JSON 문자열인 경우 파싱
      if (typeof response === 'string') {
        try {
          console.log("🔄 JSON 문자열 파싱 시도...");
          response = JSON.parse(response);
          console.log("✅ JSON 파싱 성공:", response);
        } catch (error) {
          console.error("❌ JSON 파싱 실패:", error);
          console.error("원본 문자열:", response.substring(0, 200));
          return null;
        }
      }

      // 응답이 직접 배열인 경우
      if (Array.isArray(response)) {
        console.log("✅ 프로그램매매 데이터 배열 형식:", response.length, "개");
        return response;
      }

      // 응답이 객체인 경우, 여러 가능한 키 확인
      if (response && typeof response === 'object') {
        // data 키 확인
        if (response.data && Array.isArray(response.data)) {
          console.log("✅ 프로그램매매 데이터 (data 키):", response.data.length, "개");
          return response.data;
        }

        // result 키 확인
        if (response.result && Array.isArray(response.result)) {
          console.log("✅ 프로그램매매 데이터 (result 키):", response.result.length, "개");
          return response.result;
        }

        // items 키 확인
        if (response.items && Array.isArray(response.items)) {
          console.log("✅ 프로그램매매 데이터 (items 키):", response.items.length, "개");
          return response.items;
        }

        // list 키 확인
        if (response.list && Array.isArray(response.list)) {
          console.log("✅ 프로그램매매 데이터 (list 키):", response.list.length, "개");
          return response.list;
        }

        // 객체의 첫 번째 값이 배열인지 확인
        const values = Object.values(response);
        const arrayValue = values.find(v => Array.isArray(v));
        if (arrayValue) {
          console.log("✅ 프로그램매매 데이터 (객체 첫 배열 값):", arrayValue.length, "개");
          return arrayValue;
        }
      }

      console.warn("⚠️ 프로그램매매 응답 형식이 예상과 다릅니다:", response);
      console.warn("응답 타입:", typeof response);
      console.warn("응답 키:", response ? Object.keys(response) : 'null');
      return null;
    } catch (error) {
      console.error("❌ 프로그램매매 데이터 조회 실패:", error);
      return null;
    }
  };

  // FNG 요약 데이터 조회 함수
  const fetchFngSummary = async (stockCode) => {
    try {
      const server = getServer("stock-api-server");
      if (!server || !server.isActive) {
        console.error("stock-api-server를 찾을 수 없거나 비활성 상태입니다.");
        return null;
      }

      const apiService = createApiService(server);
      const endpoint = `/api/v1/fng-summary/${stockCode}`;

      console.log(`📊 FNG Summary 조회: ${stockCode}`);
      let response = await apiService.get(endpoint);

      console.log("📊 FNG Summary 응답 원본:", response);

      // JSON 문자열인 경우 파싱
      if (typeof response === 'string') {
        try {
          response = JSON.parse(response);
        } catch (error) {
          console.error("❌ FNG Summary JSON 파싱 실패:", error);
          return null;
        }
      }

      // 응답이 직접 객체인 경우
      if (response && typeof response === 'object') {
        // data 키 확인
        if (response.data) {
          console.log("✅ FNG Summary 데이터 (data 키)");
          return response.data;
        }

        // t3320OutBlock 구조 확인
        if (response.t3320OutBlock || response.t3320OutBlock1) {
          console.log("✅ FNG Summary 데이터 (t3320 구조)");
          return response;
        }

        // 응답 자체가 데이터인 경우
        console.log("✅ FNG Summary 데이터 (직접 구조)");
        return response;
      }

      console.warn("⚠️ FNG Summary 응답 형식이 예상과 다릅니다:", response);
      return null;
    } catch (error) {
      console.error("❌ FNG Summary 데이터 조회 실패:", error);
      return null;
    }
  };

  // 외국인/기관 매매 데이터 조회 함수
  const fetchForeignInstitutionData = async (stockCode) => {
    try {
      const server = getServer("stock-api-server");
      if (!server || !server.isActive) {
        console.error("stock-api-server를 찾을 수 없거나 비활성 상태입니다.");
        return null;
      }

      const apiService = createApiService(server);
      const endpoint = `/api/v1/foreign-institution/${stockCode}`;

      console.log(`📊 외국인/기관 매매 조회: ${stockCode}`);
      let response = await apiService.get(endpoint);

      console.log("📊 외국인/기관 매매 응답 원본:", response);

      // JSON 문자열인 경우 파싱
      if (typeof response === 'string') {
        try {
          response = JSON.parse(response);
        } catch (error) {
          console.error("❌ 외국인/기관 매매 JSON 파싱 실패:", error);
          return null;
        }
      }

      // 응답이 객체인 경우
      if (response && typeof response === 'object') {
        // t1716OutBlock 배열 확인
        if (response.t1716OutBlock && Array.isArray(response.t1716OutBlock)) {
          console.log("✅ 외국인/기관 매매 데이터 (t1716OutBlock):", response.t1716OutBlock.length, "개");
          return response.t1716OutBlock;
        }

        // data 키 확인
        if (response.data && Array.isArray(response.data)) {
          console.log("✅ 외국인/기관 매매 데이터 (data 키):", response.data.length, "개");
          return response.data;
        }

        // 응답 자체가 배열인지 확인
        if (Array.isArray(response)) {
          console.log("✅ 외국인/기관 매매 데이터 (배열):", response.length, "개");
          return response;
        }
      }

      console.warn("⚠️ 외국인/기관 매매 응답 형식이 예상과 다릅니다:", response);
      return null;
    } catch (error) {
      console.error("❌ 외국인/기관 매매 데이터 조회 실패:", error);
      return null;
    }
  };

  // StockPlus 뉴스 데이터 조회 함수
  const fetchStockPlusNews = async (stockCode) => {
    try {
      const server = getServer("stock-api-server");
      if (!server || !server.isActive) {
        console.error("stock-api-server를 찾을 수 없거나 비활성 상태입니다.");
        return null;
      }

      const apiService = createApiService(server);
      const endpoint = `/api/v1/stockplus-news/${stockCode}`;

      console.log(`📰 StockPlus 뉴스 조회: ${stockCode}`);
      let response = await apiService.get(endpoint, { limit: 20 });

      if (typeof response === 'string') {
        try {
          response = JSON.parse(response);
        } catch (error) {
          console.error("❌ 뉴스 JSON 파싱 실패:", error);
          return null;
        }
      }

      if (response && response.newsItems && Array.isArray(response.newsItems)) {
        console.log("✅ StockPlus 뉴스 데이터:", response.newsItems.length, "개");
        return response.newsItems;
      }

      console.warn("⚠️ 뉴스 응답 형식이 예상과 다릅니다:", response);
      return null;
    } catch (error) {
      console.error("❌ StockPlus 뉴스 데이터 조회 실패:", error);
      return null;
    }
  };

  const handleRowClick = async (item, index) => {
    const rowId = item.rank || index;
    const isCurrentlyExpanded = expandedRowIds.includes(rowId);

    if (isCurrentlyExpanded) {
      // 이미 확장된 종목이면 닫기
      setExpandedRowIds((prev) => prev.filter((id) => id !== rowId));
    } else {
      // 확장되지 않은 종목이면 열기
      setExpandedRowIds((prev) => [...prev, rowId]);

      // 차트 데이터 로드
      if (item.shcode && selectedDate) {
        const edate = selectedDate.replace(/-/g, "");
        const cacheKey = `${item.shcode}_${edate}`;

        if (!chartDataCache[cacheKey]) {
          // 로딩 상태 설정
          setChartLoadingStates((prev) => ({
            ...prev,
            [cacheKey]: { daily: true, minute: true },
          }));

          const [dailyData, minuteData] = await Promise.all([
            fetchChartData(item.shcode, "daily", edate),
            fetchChartData(item.shcode, "minute", edate),
          ]);

          setChartDataCache((prev) => ({
            ...prev,
            [cacheKey]: {
              daily: dailyData,
              minute: minuteData,
            },
          }));

          // 로딩 상태 해제
          setChartLoadingStates((prev) => ({
            ...prev,
            [cacheKey]: { daily: false, minute: false },
          }));
        }

        // 프로그램매매 데이터 로드
        const programCacheKey = `${item.shcode}_${edate}`;
        if (!programDataCache[programCacheKey]) {
          // 로딩 상태 설정
          setProgramLoadingStates((prev) => ({
            ...prev,
            [programCacheKey]: true,
          }));

          const programData = await fetchProgramData(item.shcode, selectedDate);

          setProgramDataCache((prev) => ({
            ...prev,
            [programCacheKey]: programData,
          }));

          // 로딩 상태 해제
          setProgramLoadingStates((prev) => ({
            ...prev,
            [programCacheKey]: false,
          }));
        }

        // FNG Summary 데이터 로드
        const fngCacheKey = item.shcode;
        if (!fngDataCache[fngCacheKey]) {
          // 로딩 상태 설정
          setFngLoadingStates((prev) => ({
            ...prev,
            [fngCacheKey]: true,
          }));

          const fngData = await fetchFngSummary(item.shcode);

          setFngDataCache((prev) => ({
            ...prev,
            [fngCacheKey]: fngData,
          }));

          // 로딩 상태 해제
          setFngLoadingStates((prev) => ({
            ...prev,
            [fngCacheKey]: false,
          }));
        }

        // 외국인/기관 매매 데이터 로드
        const foreignInstCacheKey = item.shcode;
        if (!foreignInstDataCache[foreignInstCacheKey]) {
          // 로딩 상태 설정
          setForeignInstLoadingStates((prev) => ({
            ...prev,
            [foreignInstCacheKey]: true,
          }));

          const foreignInstData = await fetchForeignInstitutionData(item.shcode);

          setForeignInstDataCache((prev) => ({
            ...prev,
            [foreignInstCacheKey]: foreignInstData,
          }));

          // 로딩 상태 해제
          setForeignInstLoadingStates((prev) => ({
            ...prev,
            [foreignInstCacheKey]: false,
          }));
        }

        // StockPlus 뉴스 데이터 로드
        const newsCacheKey = item.shcode;
        if (!newsDataCache[newsCacheKey]) {
          setNewsLoadingStates((prev) => ({
            ...prev,
            [newsCacheKey]: true,
          }));

          const newsData = await fetchStockPlusNews(item.shcode);

          setNewsDataCache((prev) => ({
            ...prev,
            [newsCacheKey]: newsData,
          }));

          setNewsLoadingStates((prev) => ({
            ...prev,
            [newsCacheKey]: false,
          }));
        }
      }
    }
  };

  // 금액 포맷 함수 (억 단위 입력 → 조/억 자동 변환)
  const formatAmount = (amountInEok) => {
    if (!amountInEok || amountInEok === 0) return "-";

    const value = parseFloat(amountInEok);

    // 10,000억 이상이면 조 단위로 변환
    if (value >= 10000) {
      const jo = value / 10000;
      return jo.toFixed(2) + "조";
    }

    // 10,000억 미만이면 억 단위 유지
    return value.toFixed(2) + "억";
  };

  // FNG 데이터에서 필요한 정보 추출
  const extractFngData = (fngData) => {
    if (!fngData) return null;

    console.log("📊 extractFngData 호출, 데이터:", fngData);

    return {
      // 기본 정보
      sector: fngData.upgubunnm || "정보 없음",
      recentQuarter: fngData.t_gsym || "-",
      fiscalYear: fngData.gsyyyy || "-",
      fiscalMonth: fngData.gsmm || "-",
      fiscalYearMonth: fngData.gsym || "-",

      // 재무 비율 (T_ 예상 실적 기준)
      per: fngData.t_per ? parseFloat(fngData.t_per).toFixed(2) : "-",
      pbr: fngData.pbr ? parseFloat(fngData.pbr).toFixed(2) : "-",
      eps: fngData.t_eps ? parseFloat(fngData.t_eps).toLocaleString() : "-",
      bps: fngData.bps ? parseFloat(fngData.bps).toLocaleString() : "-",
      roe: fngData.roe ? parseFloat(fngData.roe).toFixed(2) + "%" : "-",
      roa: fngData.roa ? parseFloat(fngData.roa).toFixed(2) + "%" : "-",

      // 주당 지표
      sps: fngData.sps ? parseFloat(fngData.sps).toLocaleString() : "-",
      cps: fngData.cps ? parseFloat(fngData.cps).toLocaleString() : "-",

      // 기업가치 지표
      ebitda: fngData.ebitda ? formatAmount(parseFloat(fngData.ebitda) / 100000000) : "-",
      evebitda: fngData.evebitda ? parseFloat(fngData.evebitda).toFixed(2) : "-",

      // 외국인 비율
      foreignRatio: fngData.foreignratio ? parseFloat(fngData.foreignratio).toFixed(2) + "%" : "-",

      // 자본금 및 시가총액 (억원 단위 → 조/억 자동 변환)
      capital: formatAmount(fngData.capital),
      marketCap: formatAmount(fngData.sigavalue),

      // 배당 정보
      dividend: fngData.cashsis ? parseFloat(fngData.cashsis).toLocaleString() : "-",
      dividendYield: fngData.cashrate ? parseFloat(fngData.cashrate).toFixed(2) + "%" : "-",

      // 기타 정보 (mock으로 유지, API에서 제공하지 않음)
      institutionBuy: "-",
      individualBuy: "-",
      high52Week: "-",
      low52Week: "-",
      volume52Week: "-",
    };
  };

  const generateMockData = (item) => {
    return {
      sector:
        item.themes && item.themes.length > 0
          ? item.themes[0]
          : "업종 정보 없음",
      per: (Math.random() * 30 + 5).toFixed(1),
      pbr: (Math.random() * 3 + 0.5).toFixed(2),
      foreignBuy: (Math.random() * 100).toFixed(1) + "%",
      institutionBuy: (Math.random() * 100).toFixed(1) + "%",
      individualBuy: (Math.random() * 100).toFixed(1) + "%",
      volume52Week: Math.floor(
        Math.random() * 1000000 + 100000
      ).toLocaleString(),
      high52Week: Math.floor(Math.random() * 100000 + 50000).toLocaleString(),
      low52Week: Math.floor(Math.random() * 50000 + 10000).toLocaleString(),
    };
  };

  const generateCandleData = (candleCount = 20, isMinute = false) => {
    const data = [];
    const basePrice = 160000; // 실제 API 데이터와 유사한 가격대

    for (let i = 0; i < candleCount; i++) {
      const open = basePrice + (Math.random() - 0.5) * 10000;
      const close = open + (Math.random() - 0.5) * 5000;
      const high = Math.max(open, close) + Math.random() * 3000;
      const low = Math.min(open, close) - Math.random() * 3000;

      // 분봉인 경우 시간 생성 (09:00 ~ 15:30)
      let timeField = "";
      if (isMinute) {
        const startMinutes = 9 * 60; // 09:00
        const endMinutes = 15 * 60 + 30; // 15:30
        const totalMinutes = endMinutes - startMinutes;
        const minute =
          startMinutes + Math.floor((i / candleCount) * totalMinutes);
        const hour = Math.floor(minute / 60)
          .toString()
          .padStart(2, "0");
        const min = (minute % 60).toString().padStart(2, "0");
        timeField = `${hour}${min}`;
      }

      data.push({
        open: Math.round(open),
        high: Math.round(high),
        low: Math.round(low),
        close: Math.round(close),
        date: `2025010${Math.floor(i / 20) + 1}`.padEnd(8, "0"),
        time: timeField,
        value: Math.round(Math.random() * 1000000),
        rate: Math.round(((close - open) / open) * 100 * 100) / 100,
      });
    }
    return data;
  };

  const calculateMA = (data, period) => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    return data.map((_, index) => {
      if (index < period - 1) return null;
      const sum = data
        .slice(index - period + 1, index + 1)
        .reduce((acc, item) => acc + (parseFloat(item.close) || 0), 0);
      return Math.round(sum / period);
    });
  };

  // 외국인/기관 매매 테이블 렌더링 함수
  const renderForeignInstitutionTable = (foreignInstData) => {
    // 로딩 중이거나 데이터가 없는 경우
    if (!foreignInstData || !Array.isArray(foreignInstData) || foreignInstData.length === 0) {
      return (
        <div
          style={{
            width: "100%",
            padding: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#666",
            fontSize: "14px",
            backgroundColor: "#f9f9f9",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "8px" }}>📊</div>
            <div>외국인/기관 매매 데이터 로딩 중...</div>
          </div>
        </div>
      );
    }

    // 날짜 역순으로 정렬 (최신 날짜부터)
    const sortedData = [...foreignInstData].sort((a, b) => {
      return b.date.localeCompare(a.date);
    });

    // 최대 10개만 표시
    const displayData = sortedData.slice(0, 10);

    return (
      <div style={{ width: "100%", overflowX: "auto" }}>
        <h4 style={{
          marginBottom: "12px",
          fontSize: "14px",
          fontWeight: "bold",
          color: "#333",
          textAlign: "center"
        }}>
          외국인/기관 매매 동향 (최근 {displayData.length}일)
        </h4>
        <table style={{
          width: "100%",
          fontSize: "12px",
          borderCollapse: "collapse",
          backgroundColor: "#fff",
        }}>
          <thead>
            <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
              <th style={{ padding: "8px 4px", textAlign: "center", borderRight: "1px solid #dee2e6" }}>일자</th>
              <th style={{ padding: "8px 4px", textAlign: "right", borderRight: "1px solid #dee2e6" }}>종가</th>
              <th style={{ padding: "8px 4px", textAlign: "right", borderRight: "1px solid #dee2e6" }}>등락률</th>
              <th style={{ padding: "8px 4px", textAlign: "right", borderRight: "1px solid #dee2e6" }}>거래량</th>
              <th style={{ padding: "8px 4px", textAlign: "right", borderRight: "1px solid #dee2e6" }}>개인</th>
              <th style={{ padding: "8px 4px", textAlign: "right", borderRight: "1px solid #dee2e6" }}>기관</th>
              <th style={{ padding: "8px 4px", textAlign: "right", borderRight: "1px solid #dee2e6" }}>외국인</th>
              <th style={{ padding: "8px 4px", textAlign: "right", borderRight: "1px solid #dee2e6" }}>외인보유율</th>
              <th style={{ padding: "8px 4px", textAlign: "right" }}>공매도</th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, index) => {
              const diff = parseFloat(row.diff) || 0;
              const diffColor = diff > 0 ? '#d32f2f' : diff < 0 ? '#1976d2' : '#757575';

              // 날짜 포맷 (YYYYMMDD → MM/DD)
              const dateStr = row.date || '';
              const formattedDate = dateStr.length === 8
                ? `${dateStr.substring(4, 6)}/${dateStr.substring(6, 8)}`
                : dateStr;

              return (
                <tr key={`foreign-${index}`} style={{ borderBottom: "1px solid #e9ecef" }}>
                  <td style={{ padding: "6px 4px", textAlign: "center", borderRight: "1px solid #e9ecef" }}>
                    {formattedDate}
                  </td>
                  <td style={{ padding: "6px 4px", textAlign: "right", borderRight: "1px solid #e9ecef" }}>
                    {(row.close || 0).toLocaleString()}
                  </td>
                  <td style={{ padding: "6px 4px", textAlign: "right", color: diffColor, fontWeight: "bold", borderRight: "1px solid #e9ecef" }}>
                    {diff > 0 ? '+' : ''}{row.diff}%
                  </td>
                  <td style={{ padding: "6px 4px", textAlign: "right", borderRight: "1px solid #e9ecef" }}>
                    {((row.volume || 0) / 1000).toFixed(0)}K
                  </td>
                  <td style={{
                    padding: "6px 4px",
                    textAlign: "right",
                    color: (row.krx_0008 || 0) > 0 ? '#d32f2f' : (row.krx_0008 || 0) < 0 ? '#1976d2' : '#757575',
                    borderRight: "1px solid #e9ecef"
                  }}>
                    {((row.krx_0008 || 0) / 1000).toFixed(0)}K
                  </td>
                  <td style={{
                    padding: "6px 4px",
                    textAlign: "right",
                    color: (row.krx_0018 || 0) > 0 ? '#d32f2f' : (row.krx_0018 || 0) < 0 ? '#1976d2' : '#757575',
                    borderRight: "1px solid #e9ecef"
                  }}>
                    {((row.krx_0018 || 0) / 1000).toFixed(0)}K
                  </td>
                  <td style={{
                    padding: "6px 4px",
                    textAlign: "right",
                    color: (row.krx_0009 || 0) > 0 ? '#d32f2f' : (row.krx_0009 || 0) < 0 ? '#1976d2' : '#757575',
                    borderRight: "1px solid #e9ecef"
                  }}>
                    {((row.krx_0009 || 0) / 1000).toFixed(0)}K
                  </td>
                  <td style={{ padding: "6px 4px", textAlign: "right", borderRight: "1px solid #e9ecef" }}>
                    {row.fsc_sjrate || '-'}%
                  </td>
                  <td style={{ padding: "6px 4px", textAlign: "right" }}>
                    {((row.gm_volume || 0) / 1000).toFixed(0)}K
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // 프로그램매매 차트 렌더링 함수
  const renderProgramChart = (programData) => {
    // 로딩 중이거나 데이터가 없는 경우
    if (!programData || !Array.isArray(programData) || programData.length === 0) {
      return (
        <div
          style={{
            width: "100%",
            height: "280px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#666",
            fontSize: "14px",
            backgroundColor: "#f9f9f9",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "8px" }}>📊</div>
            <div>프로그램매매 차트 로딩 중...</div>
          </div>
        </div>
      );
    }

    // 시간순으로 정렬 (090100부터)
    const sortedData = [...programData].sort((a, b) => {
      const timeA = parseInt(a.time) || 0;
      const timeB = parseInt(b.time) || 0;
      return timeA - timeB;
    });

    // 09:01 이후 데이터만 필터링
    const filteredData = sortedData.filter((item) => {
      const time = parseInt(item.time) || 0;
      return time >= 90100; // 09:01:00
    });

    // 차트 데이터 생성 (svolume 누적)
    let cumulativeVolume = 0;
    const chartData = filteredData.map((item, index) => {
      cumulativeVolume += parseFloat(item.svolume) || 0;
      return {
        x: index,
        y: cumulativeVolume,
        originalTime: item.time,
      };
    });

    const options = {
      chart: {
        type: "line",
        height: 280,
        background: "transparent",
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: false,
        },
      },
      title: {
        text: `프로그램매매 순매수 (${filteredData.length}개)`,
        align: "center",
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          color: "#333",
        },
      },
      stroke: {
        curve: "straight",
        width: 2,
        colors: ["#2196F3"],
      },
      markers: {
        size: 0,
      },
      xaxis: {
        type: "category",
        categories: filteredData.map((item) => {
          const time = item.time || "000000";
          const hours = time.substring(0, 2);
          const minutes = time.substring(2, 4);
          return `${hours}:${minutes}`;
        }),
        tickAmount: Math.min(filteredData.length, 12),
        labels: {
          style: {
            colors: "#666",
            fontSize: "10px",
          },
          rotate: -45,
          rotateAlways: true,
          hideOverlappingLabels: false,
          trim: false,
          show: true,
          maxHeight: 80,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#666",
          },
          formatter: function (val) {
            return val.toLocaleString();
          },
        },
        title: {
          text: "누적 순매수량",
          style: {
            fontSize: "12px",
            color: "#666",
          },
        },
      },
      grid: {
        show: true,
        borderColor: "#e0e0e0",
        strokeDashArray: 0,
        position: "back",
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      tooltip: {
        enabled: true,
        x: {
          show: true,
          formatter: function (val, opts) {
            const dataPoint = filteredData[opts.dataPointIndex];
            if (dataPoint && dataPoint.time) {
              const time = dataPoint.time;
              const hours = time.substring(0, 2);
              const minutes = time.substring(2, 4);
              const seconds = time.substring(4, 6);
              return `${hours}:${minutes}:${seconds}`;
            }
            return val;
          },
        },
        y: {
          formatter: function (val) {
            return val.toLocaleString();
          },
          title: {
            formatter: () => "누적 순매수량",
          },
        },
      },
    };

    const series = [
      {
        name: "누적 순매수량",
        data: chartData.map((item) => item.y),
      },
    ];

    return (
      <div style={{ width: "100%", padding: "10px" }}>
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={280}
        />
      </div>
    );
  };

  const renderApexChart = (candleData, chartType) => {
    // 로딩 중이거나 데이터가 없는 경우
    if (!candleData || !Array.isArray(candleData) || candleData.length === 0) {
      return (
        <div
          style={{
            width: "100%",
            height: "280px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#666",
            fontSize: "14px",
            backgroundColor: "#f9f9f9",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: "8px" }}>📊</div>
            <div>{chartType === "daily" ? "일봉" : "분봉"} 차트 로딩 중...</div>
          </div>
        </div>
      );
    }

    // chartSettings가 없거나 빈 객체인 경우 기본값 사용
    const safeChartSettings = chartSettings || {};

    // 표시할 캔들 수 결정 (기본값 적용)
    const displayCandleCount =
      chartType === "daily"
        ? safeChartSettings.dailyCandleCount || 40
        : safeChartSettings.minuteCandleCount || 380;

    // 차트에 표시할 데이터 (마지막 N개)
    const displayData = candleData.slice(-displayCandleCount);

    // ApexCharts 캔들스틱 데이터 형식으로 변환 (연속적 인덱스 사용)
    const chartData = displayData.map((item, index) => ({
      x: index, // 연속적인 인덱스 사용
      y: [
        parseFloat(item.open) || 0,
        parseFloat(item.high) || 0,
        parseFloat(item.low) || 0,
        parseFloat(item.close) || 0,
      ],
      originalDate: item.date,
      originalTime: item.time,
    }));

    const options = {
      chart: {
        type: "candlestick",
        height: 280,
        background: "transparent",
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: false,
        },
      },
      title: {
        text: `${chartType === "daily" ? "일봉" : "분봉"} 차트 (${
          displayData.length
        }개)`,
        align: "center",
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          color: "#333",
        },
      },
      xaxis: {
        type: "category",
        categories: displayData.map((item, index) => {
          if (chartType === "minute" && item.time) {
            const hours = item.time.substring(0, 2);
            const minutes = item.time.substring(2, 4);
            return `${hours}:${minutes}`;
          } else if (chartType === "daily" && item.date) {
            const month = item.date.substring(4, 6);
            const day = item.date.substring(6, 8);
            return `${parseInt(month)}/${parseInt(day)}`;
          }
          return index.toString();
        }),
        tickAmount: Math.min(
          displayData.length,
          chartType === "daily" ? 8 : 12
        ),
        labels: {
          style: {
            colors: "#666",
            fontSize: "10px",
          },
          rotate: -45,
          rotateAlways: true,
          hideOverlappingLabels: false,
          trim: false,
          show: true,
          maxHeight: 80,
          showDuplicates: true,
        },
      },
      yaxis: {
        tooltip: {
          enabled: true,
        },
        labels: {
          style: {
            colors: "#666",
          },
          formatter: function (val) {
            return val.toLocaleString();
          },
        },
      },
      plotOptions: {
        candlestick: {
          colors: {
            upward: safeChartSettings.upColor || "#d32f2f",
            downward: safeChartSettings.downColor || "#1976d2",
          },
          wick: {
            useFillColor: true,
          },
        },
      },
      grid: {
        show:
          safeChartSettings.showGrid !== undefined
            ? safeChartSettings.showGrid
            : true,
        borderColor: safeChartSettings.gridColor || "#e0e0e0",
        strokeDashArray: 0,
        position: "back",
        xaxis: {
          lines: {
            show:
              safeChartSettings.showGrid !== undefined
                ? safeChartSettings.showGrid
                : true,
          },
        },
        yaxis: {
          lines: {
            show:
              safeChartSettings.showGrid !== undefined
                ? safeChartSettings.showGrid
                : true,
          },
        },
      },
      stroke: {
        width: [1, 2, 2, 2, 2, 2], // 캔들스틱: 1, 이동평균선들: 2
      },
      legend: {
        show: false, // 범례 완전히 숨김
      },
      tooltip: {
        enabled: true,
        theme: "light",
        custom: function ({ seriesIndex, dataPointIndex, w }) {
          const series = w.globals.initialSeries[seriesIndex];
          const data = series.data[dataPointIndex];

          if (!data) return '<div style="padding: 10px;">데이터 없음</div>';

          // 캔들스틱 데이터인 경우 (첫 번째 시리즈)
          if (seriesIndex === 0 && Array.isArray(data.y)) {
            const [open, high, low, close] = data.y;
            const change = close - open;
            const changePercent = ((change / open) * 100).toFixed(2);

            return `
              <div style="padding: 10px;">
                <div><strong>시가:</strong> ${open.toLocaleString()}</div>
                <div><strong>고가:</strong> ${high.toLocaleString()}</div>
                <div><strong>저가:</strong> ${low.toLocaleString()}</div>
                <div><strong>종가:</strong> ${close.toLocaleString()}</div>
                <div><strong>변동:</strong> ${
                  change >= 0 ? "+" : ""
                }${change.toLocaleString()} (${changePercent}%)</div>
              </div>
            `;
          }
          // 이동평균선 데이터인 경우
          else {
            const value = typeof data.y === "number" ? data.y : data.y;
            return `
              <div style="padding: 10px;">
                <div><strong>${
                  series.name
                }:</strong> ${value.toLocaleString()}</div>
              </div>
            `;
          }
        },
      },
    };

    // 이동평균선 데이터 계산 및 시리즈 생성
    const series = [
      {
        name: "Price",
        type: "candlestick",
        data: chartData,
      },
    ];

    // 각 이동평균선 추가 (기본값 적용)
    const maConfigs = [
      {
        period: 5,
        show:
          safeChartSettings.showMA5 !== undefined
            ? safeChartSettings.showMA5
            : true,
        color: safeChartSettings.ma5Color || "#ff9800",
        name: "MA5",
      },
      {
        period: 10,
        show:
          safeChartSettings.showMA10 !== undefined
            ? safeChartSettings.showMA10
            : true,
        color: safeChartSettings.ma10Color || "#00bcd4",
        name: "MA10",
      },
      {
        period: 20,
        show:
          safeChartSettings.showMA20 !== undefined
            ? safeChartSettings.showMA20
            : true,
        color: safeChartSettings.ma20Color || "#9c27b0",
        name: "MA20",
      },
      {
        period: 60,
        show:
          safeChartSettings.showMA60 !== undefined
            ? safeChartSettings.showMA60
            : false,
        color: safeChartSettings.ma60Color || "#4caf50",
        name: "MA60",
      },
      {
        period: 120,
        show:
          safeChartSettings.showMA120 !== undefined
            ? safeChartSettings.showMA120
            : false,
        color: safeChartSettings.ma120Color || "#f44336",
        name: "MA120",
      },
    ];

    maConfigs.forEach((config, configIndex) => {
      if (config.show) {
        // 전체 데이터로부터 이동평균선 계산 (candleData 전체 사용)
        const fullMAData = calculateMA(candleData, config.period);

        // 표시할 구간의 이동평균선 데이터만 추출 (마지막 N개)
        const displayMAData = fullMAData.slice(-displayCandleCount);

        const maSeriesData = displayMAData
          .map((value, index) => ({
            x: index,
            y: value,
          }))
          .filter((item) => item.y !== null && !isNaN(item.y) && item.y > 0);

        if (maSeriesData.length > 0) {
          const seriesConfig = {
            name: config.name,
            type: "line",
            data: maSeriesData,
            color: config.color,
            stroke: {
              width: 2,
              curve: "smooth",
            },
          };

          series.push(seriesConfig);
        }
      }
    });

    return (
      <div style={{ width: "100%", height: "280px" }}>
        <ReactApexChart
          options={options}
          series={series}
          type="candlestick"
          height={280}
        />
      </div>
    );
  };

  const renderExpandedContent = (item) => {
    const edate = selectedDate ? selectedDate.replace(/-/g, "") : "";
    const cacheKey = `${item.shcode}_${edate}`;
    const programCacheKey = `${item.shcode}_${edate}`;
    const fngCacheKey = item.shcode;
    const foreignInstCacheKey = item.shcode;

    const cachedChartData = chartDataCache[cacheKey];
    const isLoadingCharts = chartLoadingStates[cacheKey];
    const cachedProgramData = programDataCache[programCacheKey];
    const isLoadingProgram = programLoadingStates[programCacheKey];
    const cachedFngData = fngDataCache[fngCacheKey];
    const isLoadingFng = fngLoadingStates[fngCacheKey];
    const cachedForeignInstData = foreignInstDataCache[foreignInstCacheKey];
    const isLoadingForeignInst = foreignInstLoadingStates[foreignInstCacheKey];
    const newsCacheKey = item.shcode;
    const cachedNewsData = newsDataCache[newsCacheKey];
    const isLoadingNews = newsLoadingStates[newsCacheKey];

    // FNG 데이터 추출 또는 mock 데이터 사용
    const stockData = cachedFngData ? extractFngData(cachedFngData) : generateMockData(item);

    // chartSettings가 없거나 빈 객체인 경우 기본값 사용
    const safeChartSettings = chartSettings || {};

    const detailsToShow = Object.entries(visibleDetails)
      .filter(([key, visible]) => visible)
      .map(([key]) => key);

    if (detailsToShow.length === 0) {
      return (
        <div className="no-details">
          <p>표시할 상세 정보가 없습니다.</p>
          <button
            onClick={() => setShowSettings(true)}
            className="settings-btn"
          >
            설정에서 표시할 항목을 선택하세요
          </button>
        </div>
      );
    }

    // 차트 데이터 검증 및 대체
    let dailyData = cachedChartData?.daily;
    let minuteData = cachedChartData?.minute;

    // JSON 문자열인 경우 파싱
    if (typeof dailyData === "string") {
      try {
        dailyData = JSON.parse(dailyData);
      } catch (error) {
        console.error("일봉 데이터 JSON 파싱 실패:", error);
        dailyData = null;
      }
    }

    if (typeof minuteData === "string") {
      try {
        minuteData = JSON.parse(minuteData);
      } catch (error) {
        console.error("분봉 데이터 JSON 파싱 실패:", error);
        minuteData = null;
      }
    }

    // API에서 받은 데이터가 배열이 아닌 경우 처리
    if (dailyData && !Array.isArray(dailyData)) {
      // 객체 내부에 배열이 있는지 확인
      if (dailyData.data && Array.isArray(dailyData.data)) {
        dailyData = dailyData.data;
      } else if (dailyData.result && Array.isArray(dailyData.result)) {
        dailyData = dailyData.result;
      } else {
        dailyData = null;
      }
    }

    if (minuteData && !Array.isArray(minuteData)) {
      // 객체 내부에 배열이 있는지 확인
      if (minuteData.data && Array.isArray(minuteData.data)) {
        minuteData = minuteData.data;
      } else if (minuteData.result && Array.isArray(minuteData.result)) {
        minuteData = minuteData.result;
      } else {
        minuteData = null;
      }
    }

    // 로딩 중인 경우 null을 유지, 로딩이 완료되면 데이터 처리
    if (isLoadingCharts?.daily) {
      dailyData = null;
    } else if (!Array.isArray(dailyData)) {
      // 이동평균선 계산을 위해 더 많은 mock 데이터 생성 (최대 기간 + 표시 개수)
      const safeDailyCandleCount = safeChartSettings.dailyCandleCount || 40;
      const maxPeriod = Math.max(120, safeDailyCandleCount); // MA120과 표시 개수 중 최대값
      const totalMockData = safeDailyCandleCount + maxPeriod;
      dailyData = generateCandleData(totalMockData, false);
    }
    // 실제 데이터는 이미 전체 데이터로 받았으므로 slice 하지 않음

    if (isLoadingCharts?.minute) {
      minuteData = null;
    } else if (!Array.isArray(minuteData)) {
      // 이동평균선 계산을 위해 더 많은 mock 데이터 생성
      const safeMinuteCandleCount = safeChartSettings.minuteCandleCount || 380;
      const maxPeriod = Math.max(120, safeMinuteCandleCount);
      const totalMockData = safeMinuteCandleCount + maxPeriod;
      minuteData = generateCandleData(totalMockData, true);
    }
    // 실제 데이터는 이미 전체 데이터로 받았으므로 slice 하지 않음

    return (
      <div className="expanded-content">
        <div className="chart-section">
          <div className="chart-row">
            {renderApexChart(dailyData, "daily")}
            {renderApexChart(minuteData, "minute")}
          </div>
        </div>

        <div className="details-and-program-section">
          {showDetailsSection && (
            <div className="details-section">
              <table className="stock-details-table">
                <tbody>
                  <tr>
                    <td className="detail-label">업종</td>
                    <td className="detail-value">{stockData.sector}</td>
                    <td className="detail-label">PBR</td>
                    <td className="detail-value">{stockData.pbr}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">최근분기년도</td>
                    <td className="detail-value">{stockData.recentQuarter}</td>
                    <td className="detail-label">BPS</td>
                    <td className="detail-value">{stockData.bps}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">결산년월</td>
                    <td className="detail-value">{stockData.fiscalYearMonth}</td>
                    <td className="detail-label">ROA</td>
                    <td className="detail-value">{stockData.roa}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">외국인보유율</td>
                    <td className="detail-value">{stockData.foreignRatio}</td>
                    <td className="detail-label">ROE</td>
                    <td className="detail-value">{stockData.roe}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">자본금</td>
                    <td className="detail-value">{stockData.capital}</td>
                    <td className="detail-label">EBITDA</td>
                    <td className="detail-value">{stockData.ebitda}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">시가총액</td>
                    <td className="detail-value">{stockData.marketCap}</td>
                    <td className="detail-label">EV/EBITDA</td>
                    <td className="detail-value">{stockData.evebitda}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">배당금</td>
                    <td className="detail-value">{stockData.dividend}</td>
                    <td className="detail-label">SPS (주당매출액)</td>
                    <td className="detail-value">{stockData.sps}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">배당수익률</td>
                    <td className="detail-value">{stockData.dividendYield}</td>
                    <td className="detail-label">CPS (주당현금흐름)</td>
                    <td className="detail-value">{stockData.cps}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">T.PER (예상)</td>
                    <td className="detail-value">{stockData.per}</td>
                    <td className="detail-label">T.EPS (예상)</td>
                    <td className="detail-value">{stockData.eps}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className="program-chart-section">
            {renderProgramChart(cachedProgramData)}
          </div>
        </div>

        {/* 외국인/기관 매매 테이블 섹션 */}
        <div className="foreign-institution-section" style={{ marginTop: "20px" }}>
          {renderForeignInstitutionTable(cachedForeignInstData)}
        </div>

        {/* StockPlus 뉴스 섹션 */}
        <div className="news-section" style={{ marginTop: "20px" }}>
          <h4 style={{ margin: "0 0 10px 0", fontSize: "14px", color: "#333" }}>📰 종목 뉴스</h4>
          {isLoadingNews ? (
            <div style={{ textAlign: "center", padding: "15px", color: "#888" }}>뉴스를 불러오는 중...</div>
          ) : cachedNewsData && cachedNewsData.length > 0 ? (
            <div className="news-list">
              {cachedNewsData.map((news, idx) => (
                <a
                  key={news.id || idx}
                  href={news.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="news-card"
                >
                  <div className="news-title">{news.title}</div>
                  <div className="news-date">{news.createdAt}</div>
                </a>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "15px", color: "#888" }}>뉴스 데이터가 없습니다.</div>
          )}
        </div>
      </div>
    );
  };

  const renderSettingsModal = () => {
    if (!showSettings) return null;

    return (
      <div className="modal-backdrop" onClick={() => setShowSettings(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>차트 및 종목 정보 설정</h3>
            <button
              onClick={() => setShowSettings(false)}
              className="close-btn"
            >
              ×
            </button>
          </div>

          <div className="modal-tabs">
            <button
              className={`tab ${activeTab === "chart" ? "active" : ""}`}
              onClick={() => setActiveTab("chart")}
            >
              차트 설정
            </button>
            <button
              className={`tab ${activeTab === "details" ? "active" : ""}`}
              onClick={() => setActiveTab("details")}
            >
              종목 정보
            </button>
          </div>

          <div className="modal-body">
            {activeTab === "chart" ? (
              <div className="chart-settings">
                <div className="setting-group">
                  <h4>색상 설정</h4>
                  <div className="color-settings">
                    <div className="color-input">
                      <label>상승 색상:</label>
                      <input
                        type="color"
                        value={chartSettings.upColor || "#d32f2f"}
                        onChange={(e) =>
                          setChartSettings({
                            ...chartSettings,
                            upColor: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="color-input">
                      <label>하락 색상:</label>
                      <input
                        type="color"
                        value={chartSettings.downColor || "#1976d2"}
                        onChange={(e) =>
                          setChartSettings({
                            ...chartSettings,
                            downColor: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="color-input">
                      <label>심지 색상:</label>
                      <input
                        type="color"
                        value={chartSettings.wickColor || "#424242"}
                        onChange={(e) =>
                          setChartSettings({
                            ...chartSettings,
                            wickColor: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="setting-group">
                  <h4>캔들 수 설정</h4>
                  <div className="candle-count-settings">
                    <div className="candle-input-group">
                      <label>일봉:</label>
                      <input
                        type="text"
                        value={chartSettings.dailyCandleCount || ""}
                        placeholder="40"
                        onChange={(e) => {
                          const value = e.target.value;
                          // 숫자만 입력 가능
                          if (value === "" || /^\d+$/.test(value)) {
                            const num = parseInt(value);
                            if (value === "" || (num >= 0 && num <= 240)) {
                              setChartSettings({
                                ...chartSettings,
                                dailyCandleCount: value === "" ? "" : num,
                              });
                            }
                          }
                        }}
                        onBlur={(e) => {
                          // blur 시 빈 값이면 기본값 설정
                          if (e.target.value === "") {
                            setChartSettings({
                              ...chartSettings,
                              dailyCandleCount: 40,
                            });
                          }
                        }}
                      />
                      <span className="max-label">/240</span>
                    </div>
                    <div className="candle-input-group">
                      <label>분봉:</label>
                      <input
                        type="text"
                        value={chartSettings.minuteCandleCount || ""}
                        placeholder="380"
                        onChange={(e) => {
                          const value = e.target.value;
                          // 숫자만 입력 가능
                          if (value === "" || /^\d+$/.test(value)) {
                            const num = parseInt(value);
                            if (value === "" || (num >= 0 && num <= 380)) {
                              setChartSettings({
                                ...chartSettings,
                                minuteCandleCount: value === "" ? "" : num,
                              });
                            }
                          }
                        }}
                        onBlur={(e) => {
                          // blur 시 빈 값이면 기본값 설정
                          if (e.target.value === "") {
                            setChartSettings({
                              ...chartSettings,
                              minuteCandleCount: 380,
                            });
                          }
                        }}
                      />
                      <span className="max-label">/380</span>
                    </div>
                  </div>
                </div>

                <div className="setting-group">
                  <h4>이동평균선</h4>
                  <div className="ma-settings">
                    <div className="ma-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={chartSettings.showMA5 || false}
                          onChange={(e) =>
                            setChartSettings({
                              ...chartSettings,
                              showMA5: e.target.checked,
                            })
                          }
                        />
                        MA5
                      </label>
                      <input
                        type="color"
                        value={chartSettings.ma5Color || "#ff9800"}
                        onChange={(e) =>
                          setChartSettings({
                            ...chartSettings,
                            ma5Color: e.target.value,
                          })
                        }
                        disabled={!chartSettings.showMA5}
                      />
                    </div>
                    <div className="ma-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={chartSettings.showMA10 || false}
                          onChange={(e) =>
                            setChartSettings({
                              ...chartSettings,
                              showMA10: e.target.checked,
                            })
                          }
                        />
                        MA10
                      </label>
                      <input
                        type="color"
                        value={chartSettings.ma10Color || "#00bcd4"}
                        onChange={(e) =>
                          setChartSettings({
                            ...chartSettings,
                            ma10Color: e.target.value,
                          })
                        }
                        disabled={!chartSettings.showMA10}
                      />
                    </div>
                    <div className="ma-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={chartSettings.showMA20 || false}
                          onChange={(e) =>
                            setChartSettings({
                              ...chartSettings,
                              showMA20: e.target.checked,
                            })
                          }
                        />
                        MA20
                      </label>
                      <input
                        type="color"
                        value={chartSettings.ma20Color || "#9c27b0"}
                        onChange={(e) =>
                          setChartSettings({
                            ...chartSettings,
                            ma20Color: e.target.value,
                          })
                        }
                        disabled={!chartSettings.showMA20}
                      />
                    </div>
                    <div className="ma-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={chartSettings.showMA60 || false}
                          onChange={(e) =>
                            setChartSettings({
                              ...chartSettings,
                              showMA60: e.target.checked,
                            })
                          }
                        />
                        MA60
                      </label>
                      <input
                        type="color"
                        value={chartSettings.ma60Color || "#4caf50"}
                        onChange={(e) =>
                          setChartSettings({
                            ...chartSettings,
                            ma60Color: e.target.value,
                          })
                        }
                        disabled={!chartSettings.showMA60}
                      />
                    </div>
                    <div className="ma-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={chartSettings.showMA120 || false}
                          onChange={(e) =>
                            setChartSettings({
                              ...chartSettings,
                              showMA120: e.target.checked,
                            })
                          }
                        />
                        MA120
                      </label>
                      <input
                        type="color"
                        value={chartSettings.ma120Color || "#f44336"}
                        onChange={(e) =>
                          setChartSettings({
                            ...chartSettings,
                            ma120Color: e.target.value,
                          })
                        }
                        disabled={!chartSettings.showMA120}
                      />
                    </div>
                  </div>
                </div>

                <div className="setting-group">
                  <h4>격자</h4>
                  <div className="grid-settings">
                    <label>
                      <input
                        type="checkbox"
                        checked={chartSettings.showGrid || false}
                        onChange={(e) =>
                          setChartSettings({
                            ...chartSettings,
                            showGrid: e.target.checked,
                          })
                        }
                      />
                      격자 표시
                    </label>
                    <input
                      type="color"
                      value={chartSettings.gridColor || "#e0e0e0"}
                      onChange={(e) =>
                        setChartSettings({
                          ...chartSettings,
                          gridColor: e.target.value,
                        })
                      }
                      disabled={!chartSettings.showGrid}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="details-settings">
                {/* PressedPage에서만 종목정보 전체 토글 표시 */}
                {setShowDetailsSection &&
                  typeof setShowDetailsSection === "function" && (
                    <div
                      className="details-section-toggle"
                      style={{
                        marginBottom: "20px",
                        padding: "16px",
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#495057",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={showDetailsSection}
                          onChange={(e) => {
                            setShowDetailsSection(e.target.checked);
                          }}
                          style={{
                            marginRight: "10px",
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        />
                        <span
                          onClick={() => {
                            setShowDetailsSection(!showDetailsSection);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          종목 정보 영역 표시
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6c757d",
                          marginTop: "8px",
                          paddingLeft: "28px",
                        }}
                      >
                        체크 해제 시 모든 종목의 상세 정보가 숨겨집니다
                      </div>
                    </div>
                  )}

                <h4
                  style={{
                    opacity:
                      setShowDetailsSection &&
                      typeof setShowDetailsSection === "function"
                        ? showDetailsSection
                          ? 1
                          : 0.5
                        : 1,
                  }}
                >
                  표시할 종목 정보를 선택하세요
                </h4>
                <div
                  className="checkbox-grid"
                  style={{
                    opacity:
                      setShowDetailsSection &&
                      typeof setShowDetailsSection === "function"
                        ? showDetailsSection
                          ? 1
                          : 0.5
                        : 1,
                  }}
                >
                  {Object.entries(visibleDetails).map(([key, checked]) => {
                    const labels = {
                      sector: "업종",
                      per: "PER",
                      pbr: "PBR",
                      foreignBuy: "외인 매수비중",
                      institutionBuy: "기관 매수비중",
                      individualBuy: "개인 매수비중",
                      high52Week: "52주 최고",
                      low52Week: "52주 최저",
                      volume52Week: "52주 평균거래량",
                    };

                    const isDisabled =
                      setShowDetailsSection &&
                      typeof setShowDetailsSection === "function"
                        ? !showDetailsSection
                        : false;

                    return (
                      <label
                        key={`detail-${key}`}
                        className="checkbox-item"
                        style={{
                          opacity: isDisabled ? 0.5 : 1,
                          pointerEvents: isDisabled ? "none" : "auto",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked || false}
                          disabled={isDisabled}
                          onChange={(e) =>
                            setVisibleDetails({
                              ...visibleDetails,
                              [key]: e.target.checked,
                            })
                          }
                        />
                        {labels[key]}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // 화면 크기를 실시간으로 추적하는 상태
  const [windowWidth, setWindowWidth] = useState(() => {
    // 안전한 window 객체 접근
    if (typeof window !== "undefined") {
      return window.innerWidth;
    }
    return 1200; // 기본값
  });

  // 화면 크기 변화 감지
  useEffect(() => {
    // window 객체가 없으면 리스너를 등록하지 않음
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // 초기 크기 설정 (혹시 모를 차이 방지)
    setWindowWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);

    // 정리 함수에서 안전하게 제거
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 화면 크기에 따른 실제 표시되는 컬럼 수 계산
  const getVisibleColumnCount = () => {
    try {
      // 기본 컬럼 수: 순위(1) + 종목명(1) + 현재가(1) + 등락률(1) + 거래대금(1) + 거래량(1) + 시총(1) + 테마(1) = 8
      let totalColumns = 8;

      // 별 버튼이 표시되는 경우 +1
      if (showStarButton) {
        totalColumns += 1;
      }

      // 페이지별로 CSS로 강제 숨긴 컬럼 수 추가 (예: FavoritesPage의 경우 거래대금, 거래량)
      let hiddenCount = 0;
      if (hiddenColumns && Array.isArray(hiddenColumns)) {
        hiddenCount = hiddenColumns.length;
      }

      const result = Math.max(1, totalColumns - hiddenCount);

      console.log(`[${pageId}] getVisibleColumnCount: totalColumns=${totalColumns}, hiddenByCSS=${hiddenColumns?.length || 0}, result=${result}, windowWidth=${windowWidth}`);
      return result;
    } catch (error) {
      console.error("getVisibleColumnCount 에러:", error);
      return 8; // 에러 발생 시 기본값 반환
    }
  };

  const handleCollapseAll = () => {
    setExpandedRowIds([]);
  };

  return (
    <>
      <div className="trading-table-container">
        {expandedRowIds.length > 0 && (
          <div
            className="table-controls"
            style={{
              padding: "8px 16px",
              borderBottom: "1px solid #eee",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#f8f9fa",
            }}
          >
            <span
              className="expanded-count"
              style={{ fontSize: "14px", color: "#666" }}
            >
              {expandedRowIds.length}개 종목 확장됨
            </span>
            <button
              onClick={handleCollapseAll}
              className="collapse-all-btn"
              style={{
                background: "#dc3545",
                color: "white",
                border: "none",
                padding: "4px 12px",
                borderRadius: "4px",
                fontSize: "12px",
                cursor: "pointer",
              }}
            >
              전체 접기
            </button>
          </div>
        )}
        <table className="trading-table">
          <thead>
            <tr>
              {showStarButton && <th className="star" style={{ width: '50px' }}>★</th>}
              <th className="rank">순위</th>
              <th className="name">종목명</th>
              <th className="price">현재가</th>
              <th className="rate">등락률</th>
              <th className="trading-value">거래대금</th>
              <th className="volume">거래량</th>
              <th className="market-cap">시총</th>
              <th className="theme">테마</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && data.length === 0 ? (
              // 초기 로딩 시에만 skeleton 표시 (데이터가 없을 때)
              Array.from({ length: 10 }, (_, i) => (
                <tr key={`loading-${i}`}>
                  <td className="rank">
                    <div className="skeleton-text">-</div>
                  </td>
                  <td className="name">
                    <div className="skeleton-text">{initialLoadingMessage}</div>
                  </td>
                  <td className="price">
                    <div className="skeleton-text">-</div>
                  </td>
                  <td className="rate">
                    <div className="skeleton-text">-</div>
                  </td>
                  <td className="trading-value">
                    <div className="skeleton-text">-</div>
                  </td>
                  <td className="volume">
                    <div className="skeleton-text">-</div>
                  </td>
                  <td className="market-cap">
                    <div className="skeleton-text">-</div>
                  </td>
                  <td className="theme">
                    <div className="skeleton-text">-</div>
                  </td>
                </tr>
              ))
            ) : data.length > 0 ? (
              data.map((item, index) => {
                const rowId = item.rank || index;
                const isExpanded = expandedRowIds.includes(rowId);
                return (
                  <Fragment key={`row-${rowId}`}>
                    <tr
                      onClick={() => handleRowClick(item, index)}
                      className={`clickable-row ${
                        isExpanded ? "expanded" : ""
                      }`}
                    >
                      {showStarButton && (
                        <td className="star" style={{ textAlign: 'center' }}>
                          <StarButton stockCode={item.shcode || item.stock_code} size="small" />
                        </td>
                      )}
                      <td className="rank">{item.rank}</td>
                      <td className="name">
                        <div className="name-content">
                          {item.name}
                          {isExpanded && (
                            <span className="expanded-indicator"> ▼</span>
                          )}
                        </div>
                      </td>
                      <td className="price">{item.price}</td>
                      <td
                        className={`rate ${getRateColorClass(
                          item.rateValue || parseFloat(item.rate)
                        )}`}
                      >
                        {item.rate}
                      </td>
                      <td className="trading-value">{item.tradingValue}</td>
                      <td className="volume">{item.volume}</td>
                      <td className="market-cap">{item.marketCap}</td>
                      <td className="theme">
                        <div className="theme-labels">
                          {(item.themes || []).map((theme, themeIndex) => (
                            <span
                              key={`theme-${themeIndex}-${theme}`}
                              className="theme-label"
                              style={getThemeStyle(theme)}
                              title={theme}
                            >
                              {theme}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="expanded-row">
                        <td colSpan={getVisibleColumnCount()}>
                          {renderExpandedContent(item)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={getVisibleColumnCount()}
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#666",
                  }}
                >
                  조회된 데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {renderSettingsModal()}
    </>
  );
};

export default TradingTable;
